import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import type { ReportRow } from 'src/app/core/services/reports-api.service';
import { formatReportDuration } from '../report-duration.util';
import { OutcomeChip, hasOutcomeData, outcomeChips } from '../report-outcomes.util';
import { reportDetailPath } from '../reports-route.util';

export interface OutcomeState {
  kind: 'none' | 'unscored' | 'chips';
  chips: OutcomeChip[];
}

@Component({
  selector: 'app-reports-table',
  standalone: true,
  imports: [CommonModule, RouterLink, IconModule, HasPermissionPipe, ListModuleTableComponent, ListCellDefDirective],
  templateUrl: './reports-table.component.html',
})
export class ReportsTableComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: ReportRow[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No report data found. Adjust filters or deploy agents to see activity here.';
  /** Query params carried to the agent's report so its date range matches this list. */
  @Input() detailQueryParams: Record<string, string> = {};

  @Output() view = new EventEmitter<ReportRow>();

  hasDetailLink(row: ReportRow): boolean {
    return reportDetailPath(row) !== null;
  }

  detailLink(row: ReportRow): string[] | null {
    return reportDetailPath(row);
  }

  durationDisplay(row: ReportRow): string {
    return formatReportDuration(row.pd_duration_seconds, row.pd_duration_hours);
  }

  /** What the Outcomes cell shows: chips, "No PDs yet", or "Not yet scored". */
  outcomeState(row: ReportRow): OutcomeState {
    if (row.total_pds === 0) return { kind: 'none', chips: [] };
    const chips = outcomeChips(row);
    return chips.length ? { kind: 'chips', chips } : { kind: 'unscored', chips: [] };
  }

  get columns(): ListModuleColumnDef[] {
    const columns: ListModuleColumnDef[] = [
      { id: 'agent', header: 'Agent', cellClass: 'min-w-[12rem] max-w-[18rem] text-sm' },
      { id: 'client', header: 'Client', cellClass: 'max-w-[10rem] truncate text-sm' },
      { id: 'product', header: 'Product', cellClass: 'whitespace-nowrap text-sm' },
      { id: 'totalPds', header: 'PDs', align: 'center', cellClass: 'whitespace-nowrap text-center tabular-nums text-sm font-medium' },
    ];
    // An older backend sends no counts, so the column would be permanently empty.
    if (this.rows.some(hasOutcomeData)) {
      columns.push({ id: 'outcomes', header: 'Outcomes', cellClass: 'whitespace-nowrap text-sm' });
    }
    columns.push(
      { id: 'duration', header: 'Duration', align: 'center', cellClass: 'whitespace-nowrap text-center font-mono text-xs text-gray-500 tabular-nums' },
      { id: 'lastPd', header: 'Last PD', cellClass: 'whitespace-nowrap text-sm text-gray-500 dark:text-gray-400' },
      { id: 'actions', header: '', align: 'right', cellClass: 'text-end' },
    );
    return columns;
  }
}
